import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, Shield, Banknote, GraduationCap } from "lucide-react";
import { useUserRole, type AppRole } from "@/hooks/use-user-role";
import { resolveAvatarUrl } from "@/lib/avatar";
import { useCachedState } from "@/hooks/use-cached-state";

const ROLE_LABEL: Record<AppRole, string> = {
  superadmin: "Super Admin",
  admin: "Admin",
  bendahara: "Bendahara",
  guru: "Guru",
};

const ROLE_ICON: Record<AppRole, React.ReactNode> = {
  superadmin: <Shield className="w-3 h-3" />,
  admin: <Shield className="w-3 h-3" />,
  bendahara: <Banknote className="w-3 h-3" />,
  guru: <GraduationCap className="w-3 h-3" />,
};

export function UserMenu() {
  const navigate = useNavigate();
  const { role } = useUserRole();
  const [profile, setProfile] = useCachedState<{ name: string; email: string; avatar?: string | null }>("user-menu:profile", { name: "", email: "" });

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let channelUserId: string | null = null;

    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) {
        if (!user && !cancelled) setProfile({ name: "", email: "", avatar: null });
        return;
      }
      if (channelUserId !== user.id) {
        if (channel) supabase.removeChannel(channel);
        channel = null;
        channelUserId = user.id;
      }
      const { data } = await supabase.from("profiles").select("full_name, avatar_url, email").eq("id", user.id).maybeSingle();
      const avatarUrl = await resolveAvatarUrl(data?.avatar_url);
      if (cancelled) return;
      setProfile((prev) => ({
        name: data?.full_name || user.email?.split("@")[0] || "Admin",
        email: data?.email || user.email || "",
        avatar: avatarUrl ?? prev.avatar,
      }));

      if (!channel) {
        channel = supabase
          .channel(`profile-${user.id}`)
          .on("postgres_changes", { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${user.id}` }, () => load())
          .subscribe();
      }
    };

    load();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        if (channel) supabase.removeChannel(channel);
        channel = null;
        channelUserId = null;
        setProfile({ name: "", email: "", avatar: null });
        return;
      }
      if (event === "SIGNED_IN" || event === "USER_UPDATED") void load();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  const logout = async () => {
    try { await supabase.rpc("mark_offline"); } catch { /* ignore */ }
    window.dispatchEvent(new Event("almunajah:account-reset"));
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const initials = profile.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "A";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-2 rounded-full pl-1 pr-3 h-10">
          <Avatar className="w-8 h-8">
            {profile.avatar && <AvatarImage src={profile.avatar} />}
            <AvatarFallback className="bg-primary/15 text-primary text-xs font-bold">{initials}</AvatarFallback>
          </Avatar>

          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-sm font-medium">{profile.name}</span>
            {role && <span className="text-[10px] text-muted-foreground">{ROLE_LABEL[role]}</span>}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="font-semibold">{profile.name}</div>
          <div className="text-xs text-muted-foreground">{profile.email}</div>
          {role && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary">
              {ROLE_ICON[role]} {ROLE_LABEL[role]}
            </div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}><LogOut className="w-4 h-4 mr-2" /> Keluar</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
